/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateTrendingNowV2Config } from 'state/thunks/trendingnowv2';
import { setTempTrendingNowConfig } from 'state/slices/trendingnowv2'
// material
import ConfigV2Controller from 'material/expandablesection/v2/configv2controller/configv2controller'
import ExpandableSection from 'material/expandablesection/expandablesection'
import { ReactComponent as GridIcon } from 'assets/icons/grid.svg'
// content

// styles
import injectSheet from 'react-jss'
import styles from './generalsection_styles'
import HeaderTitle from './headertitle/headertitle';
import SortConfig from './sortconfig/sortconfig';
import EngagementCounters from './engagementcounters/engagementcounters';
import ArticleLayout from './articlelayout/articlelayout';


/* ========== ~~~~~~~~~~ COMMENTING CONFIG V2: GENERAL SECTION ~~~~~~~~~~ ========== */
const GeneralSection = injectSheet(styles)((props) => {
  
  const dispatch = useDispatch()
  const { generalConfig: savedGeneralConfig, trendingNowId } = useSelector(state => state.trendingNowV2.trendingNowConfig)
  const { generalConfig: tempGeneralConfig } = useSelector(state => state.trendingNowV2.tempTrendingNowConfig)
  const { isConfigValid } = useSelector(state => state.trendingNowV2)
  const [isUpdating, setIsUpdating] = useState(false)
  const [isOpened, setIsOpened] = useState(false)

  const handleToggle = () => setIsOpened(!isOpened)

  const handleSave = useCallback(async (e) => {
    e.stopPropagation()
    setIsUpdating(true)
    const generalConfigRequest = {generalConfig: {...tempGeneralConfig}, section: 'GENERAL'}
    const response = await dispatch(updateTrendingNowV2Config({ trendingNowConfig: {...generalConfigRequest}, trendingNowId }))
    if(!response.error && !response.payload.error) setIsOpened(false)
    setIsUpdating(false)
  }, [trendingNowId, dispatch, tempGeneralConfig])

  const handleCancel = useCallback(async() => {
    setIsOpened(false)
    dispatch(setTempTrendingNowConfig({
      configName: 'generalConfig',
      data: savedGeneralConfig
    }))
  }, [dispatch, savedGeneralConfig])

  const isChanged = useMemo(() => {
    const isFieldsChanged = !(JSON.stringify(tempGeneralConfig) === JSON.stringify(savedGeneralConfig));
    return isFieldsChanged
  }, [tempGeneralConfig, savedGeneralConfig])


  const handleGeneralConfigChange = useCallback((newConfigValues) => {
    dispatch(setTempTrendingNowConfig({
      configName: 'generalConfig',
      data: {
        ...tempGeneralConfig,
        ...newConfigValues
      }
    }))
}, [dispatch, tempGeneralConfig]);

  const controller = (
    <ConfigV2Controller
      isOpened={isOpened}
      handleToggle={handleToggle}
      handleCancel={handleCancel}
      handleSave={handleSave}
      isProcessing={isUpdating}
      disableSave={!isChanged || isConfigValid.generalConfig === false}
      saveTitle={'Save Changes'}
      icon={<GridIcon />}
      title='General'
    />
  );


  const generalConfigData = useMemo((section) => {
    return {
      headerTitle: {
        enableHeader: tempGeneralConfig.enableHeader,
        enableHeaderBorder: tempGeneralConfig.enableHeaderBorder,
        headerTitle: tempGeneralConfig.headerTitle,
        headerIcon: tempGeneralConfig.headerIcon
      },
      sortConfig: tempGeneralConfig.sortConfig,
      engagementCounters: tempGeneralConfig.engagementCounters,
      layoutConfig: tempGeneralConfig.layoutConfig
    }
  }, [tempGeneralConfig])

  return (
    <ExpandableSection
      className={`expandablesectionv2 generalsection ${props.classes.generalsection}`}
      controller={controller}
      isOpened={isOpened}
      handleToggle={handleToggle}
      handleCancel={handleCancel}
      handleSave={handleSave}
      isProcessing={savedGeneralConfig.isUpdating}
      disableSave={!isChanged}
    >
      <HeaderTitle {...generalConfigData.headerTitle} onChange={handleGeneralConfigChange} />
      <SortConfig {...generalConfigData.sortConfig} onChange={handleGeneralConfigChange} />
      <EngagementCounters {...generalConfigData.engagementCounters} onChange={handleGeneralConfigChange} />
      <ArticleLayout {...generalConfigData.layoutConfig} onChange={handleGeneralConfigChange} />
    </ExpandableSection>
  )
})

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default GeneralSection;